/*
global DataDogService
*/
import _ from 'lodash';
import { DEBUG_PANEL_CITY_LAT, DEBUG_PANEL_CITY_LONG } from 'helpers/ContentHelper';
import { marineAPIKeyEnabled, previewModeEnabled, isRedirectDisabled } from 'helpers/ConfigHelper';
import { getCookie } from 'helpers/Storage';
import { getUserDevice } from 'helpers/BreakpointHelper';
import { getLocationCookies } from 'helpers/GeoLocationHelper';
import { getRefreshRedirectSignIn } from 'helpers/RouteHelper';
import { clearCache } from 'helpers/auth.clientless';
import { getAccountCookie } from 'helpers/Account';
import {
  logError,
  EVENT_TYPE_LOGOUT_REFRESH,
  EVENT_TYPE_UNAUTHORIZED,
} from 'helpers/LogReportHelpers';
import { replaceScreenUrl as OptimizelyReplaceScreenUrl } from './OptimizelyHelper';

function baseContentUrl() {
  const overrideBaseName = getCookie({ name: 'DEBUG_PANEL_API_BASEURL' });
  const overrideBaseUrl = _.get(window.ExternalConfig.api, `content.baseUrls.${overrideBaseName}`);
  const defaultBase = _.get(window.ExternalConfig, 'api.content.baseUrls.default');
  const tomatoBase = _.get(window.ExternalConfig, 'api.content.baseUrls.Tomato');

  function baseUrl() {
    return overrideBaseUrl || tomatoBase || defaultBase;
  }
  return baseUrl;
}

export const getBaseContentUrl = baseContentUrl();

const getNetworksParam = () => null;
const getPremiumPackageParam = () => null;

const getGeoLatLongParams = () => {
  const latitude = getCookie({ name: DEBUG_PANEL_CITY_LAT });
  const longitude = getCookie({ name: DEBUG_PANEL_CITY_LONG });
  const { latitude: geoCookieLatitude, longitude: geoCookieLongitude } = getLocationCookies() || {};

  const debugLocationAvailable = latitude && longitude;
  const geoCookieLocationAvailable = geoCookieLatitude && geoCookieLongitude;
  const locationCoordinatesAvailable = debugLocationAvailable || geoCookieLocationAvailable;

  if (!locationCoordinatesAvailable) return null;

  if (debugLocationAvailable) {
    return `geoLatitude=${latitude}&geoLongitude=${longitude}`;
  }
  return `geoLatitude=${geoCookieLatitude}&geoLongitude=${geoCookieLongitude}`;
};

const helperAddParams = apiURL => {
  const networks = getNetworksParam();
  const geoLatLong = getGeoLatLongParams();
  const premiumPackage = getPremiumPackageParam();
  const params = [networks, geoLatLong, premiumPackage];
  const queryString = params.filter(i => i).join('&');
  const apiURLParts = _.split(apiURL, '?');
  const paramsPreExist = apiURLParts.length >= 2;

  if (!queryString) return apiURL;
  if (paramsPreExist) return `${apiURLParts[0]}?${queryString}&${apiURLParts[1]}`;
  return `${apiURL}?${queryString}`;
};

/**
 * Return an Api Key imported from the external config
 * @return {String}             - API Key of a given Environment/Platform
 */
export const helperGetApiKey = () => {
  const userDevice = getUserDevice();
  const defaultKey = _.get(window.ExternalConfig, 'api.keys.desktop');
  const deviceKey = _.get(window.ExternalConfig, `api.keys.${userDevice}`, defaultKey);
  const overrideKey =
    marineAPIKeyEnabled() && _.get(window.ExternalConfig, `api.marine.${userDevice}`);
  const apiKey = overrideKey || deviceKey;

  return apiKey;
};

/**
 * Return Api Headers for the given call, based on the content of the external config
 * @param  {Object} opts
 * @param  {String} pathKey     - Relative path for the API call
 * @param  {String} accessToken - header accessToken for the API call
 * @param  {String} apiKey      - header apiKey for the API call
 * @param  {String} deviceId    - header deviceId for the API call
 * @param  {String} segmentId   - header segmentId for the API call
 * @return {Object}             - API Key of a given Environment/Platform
 */

export const helperGetApiHeaders = ({ pathKey, accessToken, apiKey, deviceId, segmentId } = {}) => {
  const inStatic = _.get(window.ExternalConfig, `api.static.${pathKey}`);
  const apiKeyHeaderName = 'x-api-key';
  const accessTokenHeaderName = 'Authorization';
  const skipApiKey =
    !{}.hasOwnProperty.call(window.ExternalConfig.api.content, pathKey) && inStatic;
  if (skipApiKey) return null;
  let headers = {};
  headers = { [apiKeyHeaderName]: helperGetApiKey() };
  if (previewModeEnabled()) headers['x-dcg-publish-state'] = 'preview';
  if (accessToken) headers[accessTokenHeaderName] = `Bearer ${accessToken}`;
  if (apiKey) headers[apiKeyHeaderName] = apiKey;
  if (deviceId) headers['x-dcg-udid'] = deviceId;
  if (segmentId) headers['x-dcg-segment-id'] = segmentId;

  return { headers };
};

/**
 * Return Api Headers for the given call, based on the content of the external config
 * @param  {Object} opts
 * @param  {String} pathKey     - Relative path for the API call
 * @param  {String} accessToken - header accessToken for the API call
 * @param  {String} apiKey      - header apiKey for the API call
 * @param  {String} deviceId    - header deviceId for the API call
 * @param  {String} segmentId   - header segmentId for the API call
 * @param {Boolean} checkToken  -  designate if the accessToken needs to refreshed
 * @param {Object} profile      - user profile object
 * @return {Object}             - API Key of a given Environment/Platform
 */

export const helperGetApiHeadersv2 = async ({
  apiKey,
  checkToken = false,
  deviceId,
  pathKey,
  profile,
  requiresAuth = false,
  segmentId,
} = {}) => {
  if (checkToken && requiresAuth && profile) {
    await profile?.checkRefreshToken();
  }
  const accessToken = getAccountCookie()?.accessToken;
  const inStatic = _.get(window.ExternalConfig, `api.static.${pathKey}`);
  const apiKeyHeaderName = 'x-api-key';
  const accessTokenHeaderName = 'Authorization';
  const skipApiKey =
    !{}.hasOwnProperty.call(window.ExternalConfig.api.content, pathKey) && inStatic;
  if (skipApiKey) return null;
  let headers = {};
  headers = { [apiKeyHeaderName]: helperGetApiKey() };

  if (previewModeEnabled()) headers['x-dcg-publish-state'] = 'preview';
  if (requiresAuth && accessToken) headers[accessTokenHeaderName] = `Bearer ${accessToken}`;
  if (apiKey) headers[apiKeyHeaderName] = apiKey;
  if (deviceId) headers['x-dcg-udid'] = deviceId;
  if (segmentId) headers['x-dcg-segment-id'] = segmentId;

  return { headers };
};

export const helperConvertPathToApiRoute = path => `${getBaseContentUrl()}${path}`;

/**
 * Return the full Api URL based on the path provided
 * @param  {String} pathKey     - Relative path for the API call
 * @return {String}             - Absolute path for the API call
 */
export const helperGetApiRoute = pathKey => {
  const getContentUrl = pathString => {
    const pathKeyRoute = _.get(window.ExternalConfig, `api.content.${pathString}`);
    if (pathKeyRoute) {
      const replaceUrl = OptimizelyReplaceScreenUrl(pathKey);
      if (pathKeyRoute.indexOf('https') > -1) {
        // "upNext": "https://api-staging.fox.com/fbc-content/_qa_v3/epglistings/{epgListingId}/next"
        // currently has different base url
        // allows for paths to be overwritten by config if full url included
        return pathKeyRoute;
      }
      return `${getBaseContentUrl()}${replaceUrl || pathKeyRoute}`;
    }
    return false;
  };

  let apiURL =
    getContentUrl(pathKey) ||
    _.get(window.ExternalConfig, `api.static.${pathKey}`) ||
    `${getBaseContentUrl()}screens/${pathKey}`;

  apiURL = helperAddParams(apiURL);
  return apiURL;
};

export const helperConvertApiRoute = uri => {
  if (!uri) return null;
  const tests = [
    { txt: '/screens/', base: getBaseContentUrl(), offset: 1 },
    { txt: '/seasons/', base: getBaseContentUrl(), offset: 1 },
    { txt: '/screenpanels/', base: getBaseContentUrl(), offset: 1 },
    // note: intentionally doesn't have trailing slash "/"
    { txt: '/search', base: getBaseContentUrl(), offset: 1 },
  ];

  let newUri = uri;
  _.forEach(tests, test => {
    const { txt, base, offset } = test;
    const txtLocation = uri.indexOf(txt);
    if (txtLocation > 0) {
      const relativeScreen = uri.substr(txtLocation + offset);
      if (txtLocation <= 0 || !relativeScreen) return;
      newUri = `${base}${relativeScreen}`;
    }
  });

  newUri = helperAddParams(newUri);
  return newUri;
};

/**
 * Return the full Api URL based on the path provided
 * @param  {String} pathKey     - Relative path for the API call
 * @param  {String} id          - Resource Identifier
 * @param  {String} placeholder - String in API path that needs to be replaced by id
 * @return {String}             - Absolute path for the API call
 */
export const helperGetApiRouteById = (pathKey, id, placeholder) => {
  if (!id) {
    return helperGetApiRoute(pathKey);
  }
  const contentHasKey = {}.hasOwnProperty.call(window.ExternalConfig.api.content, pathKey);
  const staticList = _.get(window.ExternalConfig, 'api.static');
  let apiRouteURL;
  if (!apiRouteURL && contentHasKey) {
    const contentPath = window.ExternalConfig.api.content[pathKey];
    const route =
      contentPath.indexOf('https') > -1 ? contentPath : `${getBaseContentUrl()}${contentPath}`;
    apiRouteURL = route.replace(placeholder, id);
  }
  if (!apiRouteURL && staticList) {
    const staticHasKey = {}.hasOwnProperty.call(staticList, pathKey);
    const staticHasKeyAndId =
      staticHasKey &&
      typeof staticList[pathKey] === 'object' &&
      {}.hasOwnProperty.call(staticList[pathKey], id);
    if (staticHasKeyAndId) {
      apiRouteURL = staticList[pathKey][id];
    } else if (staticHasKey) {
      apiRouteURL = staticList[pathKey];
    }
  }

  if (!apiRouteURL) apiRouteURL = `${getBaseContentUrl()}${pathKey}/${id}`;

  apiRouteURL = helperAddParams(apiRouteURL);

  return apiRouteURL;
};

/**
 * Return the full API route for Series screenURL with provided showId
 * @param  {String} showId      - showId to be added to the mvpds route
 * @return {String}             - Absolute path for the API call
 */

export const helperGetApiSeriesUrlRoute = ({ showId, showCode }) => {
  const showsQueryRoute = _.get(window.ExternalConfig, 'api.content.showsQuery');
  const seriesRoute = `${getBaseContentUrl()}${showsQueryRoute}`;
  let route = `${seriesRoute}/${showId}`;
  if (showCode) {
    route = `${seriesRoute}?showCode=${showCode}`;
  }
  return helperAddParams(route);
};

export const helperGetApiCategoryUrlRoute = ({ id, isPersonality }) => {
  const query = isPersonality ? 'personalitiesQuery' : 'destinationQuery';
  const categoryQueryRoute = window?.ExternalConfig?.api?.content?.[query];
  const categoryIdRoute = categoryQueryRoute.replace('{categoryId}', id);
  const route = `${getBaseContentUrl()}${categoryIdRoute}`;
  return helperAddParams(route);
};

/**
 * Return the full account route with provided params
 * @param  {String} pathKey     - params to be added to the mvpds route
 * @return {String}             - Absolute path for the API call
 */

export const helperGetApiAccountRoute = pathKey => {
  const pathKeyRoute = _.get(window.ExternalConfig, `api.profiles.${pathKey}`);

  return `${getBaseContentUrl()}${pathKeyRoute}`;
};

export const helperGetFavoriteDetailsApiRoute = favoritesShowIds => {
  if (!favoritesShowIds && !favoritesShowIds.length) return null;

  const params = [];
  params.push(`showCode=${favoritesShowIds}`);

  const apiRoute = helperGetApiRoute('showsQuery');
  const networksParams = getNetworksParam();
  let url = apiRoute.replace(networksParams, '');
  url = url.replace(/\?\s*$/, '');
  const apiURLParts = _.split(url, '?');
  const paramsPreExist = apiURLParts.length >= 2;
  if (paramsPreExist) return `${apiURLParts[0]}?${params.join('&')}&${apiURLParts[1]}`;

  return `${url}?${params.join('&')}`;
};

export const helperGetLiveUpNextUrl = ({ videoId }) => {
  const pageType = 'upNextTomato';
  const placeholder = '{epgListingId}';

  const url = helperGetApiRouteById(pageType, videoId, placeholder);
  return { url, pageType };
};

export const apiErrorHandler = async ({ error, profile }) => {
  const statusCode = error?.response?.status ?? error?.statusCode ?? error?.errorCode;
  let refreshAccount;
  const playerEntitlementsError = error?.entitlementIssues?.[0]?.errorCode;
  // The request was made and the server responded with a status code
  // that falls out of the range of 2xx
  const isArticle = window?.location?.href?.includes('/article/');
  if (isArticle) {
    return error;
  }

  if (!refreshAccount?.status) {
    return error;
  }
  switch (statusCode) {
    case 400:
    case 401:
    case 403:
      if (
        playerEntitlementsError !== 1000 &&
        playerEntitlementsError !== 1002 &&
        playerEntitlementsError !== 1003 &&
        playerEntitlementsError !== 1005 &&
        playerEntitlementsError !== 1007 &&
        playerEntitlementsError !== 1013 &&
        !isRedirectDisabled()
      ) {
        clearCache();
        if (statusCode === 401) {
          logError(error, {
            event: 'debug',
            message: EVENT_TYPE_UNAUTHORIZED,
            service: DataDogService,
          });
          if (profile) {
            try {
              refreshAccount = await profile?.checkRefreshToken();
              if (refreshAccount?.status) {
                window.location.reload();
              }
            } catch (e) {
              logError(e, {
                event: 'debug',
                message: EVENT_TYPE_LOGOUT_REFRESH,
                service: DataDogService,
              });
              getRefreshRedirectSignIn();
            }
          }
        }
      }
      break;
    default:
      break;
  }

  return error;
};
